import { useContext } from 'react'

import { AuthContext } from '@/lib/auth-context-instance'
import {
  PERMISSION_DENIED_HINT,
  hasPermission,
  type PermissionCode,
} from '@/lib/permissions'

/**
 * SOLY-PERM-001 — les droits de la session, tels que le front doit les lire.
 *
 * `can` répond oui/non ; `reasonIfDenied` donne la phrase à mettre en `title` sur un bouton grisé.
 * Les deux vont ensemble : un bouton désactivé sans explication est pire qu'un bouton absent.
 *
 * **Le back reste l'autorité.** Ce hook masque et grise, il ne protège rien : chaque action a sa
 * garde `[RequirePermission]` côté API, verrouillée par `PlanningPermissionCoverageTests`. Ce qui se
 * joue ici est l'expérience — ne pas proposer un geste qui finira en 403.
 *
 * Hors provider (tests de composants montés isolément), tout est accordé : c'est le comportement
 * d'avant la feature, et laisser un composant se griser tout seul parce qu'il lui manque un contexte
 * serait un faux négatif bien plus coûteux qu'un vrai. Dans l'application, `AuthProvider` enveloppe
 * la racine — le cas ne s'y produit pas.
 */
export function usePermissions() {
  const auth = useContext(AuthContext)
  const user = auth?.user ?? null
  const outsideProvider = auth === undefined

  const can = (code: PermissionCode) => outsideProvider || hasPermission(user, code)

  return {
    can,
    /** `undefined` quand le droit est accordé — se branche directement sur `title={...}`. */
    reasonIfDenied: (code: PermissionCode) => (can(code) ? undefined : PERMISSION_DENIED_HINT[code]),
  }
}
